import { FormattedMessage } from 'react-intl';
import { useSuspenseQuery } from '@tanstack/react-query';
import { useSentry } from '@repo/shared-kit/sentry';
import { useTrackRender } from '@repo/shared-kit/hooks';
import { useCurrentDevice } from '@repo/shared-kit/hooks';
import { kycQueries, KycProvider } from '@repo/api-kit/queries/due-diligence/kyc';
import { Button } from '@repo/design-system-kit';
import { useConfig } from '@repo/shared-kit/config';
import { IdnowProvider } from '../idnow-provider';
import { FourthlineProvider } from '../fourthline-provider';
import { getProviderType } from '../utils';
import { useKycContext } from '../context';
import { KycError } from '../error';
import styles from './page.module.css';

const providerComponents: Partial<Record<keyof typeof KycProvider, React.ReactNode>> = {
  [KycProvider.idnow]: <IdnowProvider />,
  [KycProvider.fourthline]: <FourthlineProvider />,
};

/**
 * Main KYC page component that renders the appropriate provider based on the KYC response.
 * Determines which verification provider (IDnow or Fourthline) to display.
 *
 * @returns The provider component or null if provider is unspecified
 */
export function KycPage() {
  const { membershipId, userId, trackingPrefix, legalCountry, isRegisterFlow, onComplete } =
    useKycContext();
  const market = isRegisterFlow ? legalCountry : undefined;
  const { data: kycInfo, refetch } = useSuspenseQuery(kycQueries.item(market));
  const { isMobileBrowser } = useCurrentDevice();

  const providerType = getProviderType(kycInfo);

  const sentry = useSentry();
  const hasInvalidProviderType = !providerComponents[providerType];

  const { environment } = useConfig();
  const canSkipKyc = !environment.isProduction;

  useTrackRender({
    eventName: `${trackingPrefix}_kyc_started`,
    params: {
      membership_id: membershipId,
      provider: providerType,
      is_webview: isMobileBrowser,
      user_id: userId,
      is_new_kyc: true,
      type: kycInfo.activeProcess?.type,
      // FIXME: Should be deprecated in upcoming feature
      flow: kycInfo.activeProcess?.type,
    },
  });

  if (hasInvalidProviderType) {
    sentry.captureException(new Error(`Unsupported KYC provider: ${providerType}`), {
      extra: {
        kycInfo,
        membershipId,
      },
      tags: {
        component: 'KycPage',
      },
    });

    return <KycError onRetry={refetch} />;
  }

  return (
    <>
      {providerComponents[providerType]}
      {canSkipKyc ? (
        <Button
          className={styles.qaButton}
          onClick={() => {
            onComplete(providerType);
          }}
          stretch
          type="button"
          variant="secondary"
        >
          <FormattedMessage
            defaultMessage="QA Tool | Complete KYC"
            id="due-diligence-kit.kyc.skip.cta"
          />
        </Button>
      ) : null}
    </>
  );
}
